import React from 'react';
import moment from 'moment';
import { Browser } from '@capacitor/browser';
import NativeOrWeb from '../../../utils/native';
import { translateMessage } from '../../../utils/locale';
import { getAgeFromDob } from '../../../utils/date';
import { setClientIdLocalStorage } from '../../../utils/client-navigation';
import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import { connectionStatusOf } from './clientPresentation';
import { canClientAction } from './clientEntitlements';
import {
  RECRUITMENT_HOT_TOPIC_CATEGORY_ID,
  RECRUITMENT_INCOME_SIM_ITEM_ID,
  RECRUITMENT_MDRT_ITEM_ID
} from '../../../constants/recruitment';
import type { IntlLike } from './clientPresentation';
import type { ClientFactsheet, QueueClient } from '../../../types/client';
import { SUPPORT_WHATSAPP } from '../../../utils/planUpgradeContact';

export type ClientDrawerAction =
  | 'portfolio'
  | 'add-policy'
  | 'export'
  /**
   * 救命紙 straight to the sheet's own editor, rather than to the export menu
   * that offers it among others. The row's shortcut takes this door.
   */
  | 'sos-edit'
  | 'analysis'
  | 'factsheet'
  | 'smart-idea'
  /** The same dialog as 'smart-idea', opened marked on its 性格與溝通 card. */
  | 'smart-idea-disc'
  | 'meeting-note'
  /**
   * The same dialog as 'meeting-note', opened with its recorder already up so
   * the adviser's next tap is 開始錄音. The row's shortcut takes this door.
   */
  | 'meeting-record'
  /** The same dialog as 'meeting-note', opened onto the 最近會面 row. */
  | 'meeting-date'
  /** The same dialog again, opened onto the 提醒我 row. */
  | 'meeting-reminder'
  | 'tracking'
  | 'tracking-summary'
  | 'contact'
  | 'reminder'
  | 'referral'
  | 'product-recommendation'
  | 'ai'
  /**
   * 招募 view's destinations. Every one of them already exists somewhere
   * in the app; what is new is reaching them from the client the adviser is
   * looking at, rather than from the team-level 招募 page.
   *
   * None is withheld from a 已中斷 client (clientEntitlements.ts). They read
   * adviser- and team-owned MATERIAL — a chart library, a calculator, a
   * content page, a team profile — and touch none of the client's policy data,
   * which is the only thing a disconnection takes away.
   */
  | 'recruit-materials'
  | 'mdrt-calculator'
  | 'income-simulator'
  | 'recruit-hot-topic'
  | 'team-hub'
  | 'team-bot'
  | 'ai-coach'
  // The same /gpt-options as 'ai', told which conversation it was opened for.
  | 'ai-recruit'
  | 'iiqe-study'
  | 'recruit-quiz'
  /** 保障小測驗 — the business-side quiz. Handled inside the drawer, like 'recruit-quiz'. */
  | 'cover-quiz'
  | 'delete';

export type HistoryLike = {
  push: (location: unknown, state?: unknown) => void;
  replace: (location: unknown) => void;
  goBack?: () => void;
  /** Both used by the move-policy exit, which has to drop two entries. */
  go?: (delta: number) => void;
  listen?: (listener: (...args: unknown[]) => void) => () => void;
};

export type ClientActionDependencies = {
  adviserEmail?: string;
  history?: HistoryLike;
  location?: Record<string, unknown>;
  intl: IntlLike;
  userDetails?: {
    name?: string;
  };
  toggleDialog: (dialog: string, content: Record<string, unknown>) => unknown;
  mergeDialog: (dialog: string, content: Record<string, unknown>) => unknown;
  updateFactSheet: (payload: Record<string, unknown>) => unknown;
  updateViewingClient: (client: QueueClient) => unknown;
  getClientDetails: (payload: Record<string, unknown>) => unknown;
  resetAnalysisDefaultValues: () => unknown;
  requirePro: (payload?: Record<string, unknown>) => boolean;
  /** A.I. 智能助理 is open to this adviser (FEATURE_GPT or Ultra). Absent reads as true. */
  aiAvailable?: boolean;
  /** Shows the Ultra upgrade dialog unless the plan is Ultra; true when it is. */
  requireUltra?: (payload?: Record<string, unknown>) => boolean;
  /** GA4 support-channel event (utils/tracking ga4TrackSupport), as the other WhatsApp-us buttons send. */
  trackSupport?: (properties: Record<string, unknown>) => unknown;
  onProductRecommendation: (client: QueueClient) => void;
  track?: (event: string, properties?: Record<string, unknown>) => void;
};

/**
 * ClientEntry historically reads client.factsheetId directly. ClientV2 resolves
 * the adviser's own factsheet first and passes a normalized copy into those
 * existing dialogs/routes, so a shared client cannot leak another adviser's
 * data. The resolution is deliberately NOT connection-gated — the factsheet is
 * adviser-owned and survives a disconnection (utils/client-factsheet.ts).
 */

/**
 * A WhatsApp to us asking to switch A.I. on, written out so the adviser only
 * presses send. Prefixed (APP) / (WEB) like the support sheet's messages, so
 * customer service can tell where it came from.
 */
export const aiAccessWhatsAppUrl = (intl: IntlLike): string => {
  const channel = NativeOrWeb.isNativePlatform() ? '(APP)' : '(WEB)';
  const text = `${channel} ${intl.formatMessage({ id: 'ai-access-whatsapp-msg' })}`;
  return `https://api.whatsapp.com/send?phone=${SUPPORT_WHATSAPP}&text=${encodeURIComponent(text)}`;
};

export const actionableClientOf = (client: QueueClient, adviserEmail?: string): QueueClient => ({
  ...client,
  factsheetId: adviserFactsheetOf(client, adviserEmail)
});

const incomeDataOf = (factsheet: ClientFactsheet | undefined) => ({
  monthlyExpense: factsheet?.monthlyExpense,
  monthlyIncome: factsheet?.monthlyIncome,
  debt: factsheet?.debt,
  age: getAgeFromDob(factsheet?.dob, factsheet?.age)
});

const isSyntheticEmail = (email: string | null | undefined, clientId: string): boolean =>
  Boolean(email && email.includes(clientId));

const greetingMessage = (client: QueueClient, language: string): string => {
  const factsheet = client.factsheetId;
  return [
    translateMessage('Happy Birthday', language, { name: factsheet?.name || '' }),
    translateMessage('wish_message', language),
    translateMessage('prepared_gift', language)
  ].join('\n');
};

const meetingDialogContent = (
  client: QueueClient,
  mode: 'note' | 'summary',
  /**
   * Field the caller opened the dialog *for*. MeetingDialog marks that one row
   * so the adviser lands looking at what they tapped rather than searching a
   * form of eight for it. Omitted for every other entry point, which is what
   * keeps their behaviour unchanged.
   */
  focusField?: string
): Record<string, unknown> => {
  const factsheet = client.factsheetId;
  const tracking = client.trackingId;
  return {
    mode,
    client,
    focusField,
    ...(mode === 'note'
      ? {
          latestMeetingDate: factsheet?.latestMeetingDate ? moment.parseZone(factsheet.latestMeetingDate) : null
        }
      : {
          met: tracking?.met ?? 0,
          opportunity: tracking?.opportunity || '',
          businessVol: tracking?.businessVol ?? '',
          trackingStartDate: tracking?.startDate
        }),
    lastMeetingLocation: factsheet?.lastMeetingLocation || '',
    fsReminderDate: factsheet?.fsReminderDate ?? null,
    fsReminderActive: factsheet?.fsReminderActive || false,
    remark: factsheet?.remark || '',
    tags: factsheet?.tags || [],
    isRecruitment: factsheet?.isRecruitment || false,
    nextMeetingDate: factsheet?.nextMeetingDate
  };
};

export const createClientActionHandler = (deps: ClientActionDependencies) => {
  const selectClient = (client: QueueClient) => {
    setClientIdLocalStorage(client._id);
    deps.updateViewingClient(client);
  };

  const goPortfolio = (client: QueueClient) => {
    if (!deps.history) return;
    selectClient(client);
    const clientFactsheetDetail = { clientIncData: incomeDataOf(client.factsheetId) };
    localStorage.setItem('factsheetDOB', JSON.stringify(client.factsheetId?.dob || ''));
    deps.history.push({ pathname: '/dashboard', state: { clientFactsheetDetail, client } });
  };

  const goAddPolicy = (client: QueueClient) => {
    if (!client.consent) {
      deps.toggleDialog('addPortfolioHint', { client });
      return;
    }
    if (!deps.history) return;
    selectClient(client);
    localStorage.setItem('factsheetDOB', JSON.stringify(client.factsheetId?.dob || ''));
    sessionStorage.removeItem('editPolicyDetails');
    sessionStorage.removeItem('editPolicy');
    localStorage.removeItem('addRider');
    localStorage.removeItem('previousPolicyData');
    localStorage.removeItem('insurer');
    deps.history.push({
      pathname: '/add_portfolio',
      state: { clientFactsheetDetail: { clientIncData: { clientStatus: connectionStatusOf(client).status } } }
    });
  };

  const goAnalysis = (client: QueueClient) => {
    if (!deps.history) return;
    deps.resetAnalysisDefaultValues();
    selectClient(client);
    [
      'lifeAge',
      'FamilySupport',
      'extraMoney',
      'whatIfProtectionAmount',
      'whatIfProtectionAmountOfCi',
      'debt',
      'monthlyExpense',
      'monthlyIncome',
      'savingTabMonthlyIncome',
      'savingReturnRate',
      'savingInflationRate',
      'savingTabSavingTerms',
      'savingTabShowclientueAt',
      'savingRetirementTerms',
      'savingRetireAfter',
      'savingwhatIfTopUpMonthlySaving',
      'savingCurrentAsset',
      'includeMortageAmount'
    ].forEach(key => localStorage.removeItem(key));
    sessionStorage.removeItem('useTotalLife');
    deps.history.push({
      pathname: '/need-analysis',
      state: {
        clientFactsheetDetail: {
          clientIncData: { ...incomeDataOf(client.factsheetId), currentSlide: 0 }
        },
        client
      }
    });
  };

  /**
   * 救命紙 — the export menu, whose first card is the emergency sheet itself.
   * One destination for every client, exactly as V1 reaches this page.
   *
   * This briefly diverted a client with "no policies on file" to the bulk
   * policy-entry page, to spare them an empty emergency sheet. The predicate
   * behind it could not support the decision: the only policy figures the
   * server sends are the five category aggregates (server/utils/client.js
   * :495-508 — the raw `policies` array is reduced and dropped, no total
   * survives), and those cover 5 of the 12 subcategories in
   * constants/insuranceCategories.js. A client holding only motor/travel/home/
   * accident/mortgage cover read as empty and lost this page altogether, since
   * goExport is ClientV2's only route to it. So did every 已中斷 client, whose
   * aggregates the server withholds behind `dataAccess` — there, "absent" means
   * "not allowed to see", not "none". Restoring the nudge needs a real count
   * from the server first.
   *
   * sessionStorage: ExportOptions' own handlers read the client back out of it
   * (ExportOptions.js:33, :42).
   */
  const goExport = (client: QueueClient) => {
    if (!deps.history) return;
    selectClient(client);
    sessionStorage.setItem('client', JSON.stringify(client));
    deps.history.push({ pathname: '/export_options', state: {} });
  };

  /**
   * 救命紙 from a client row — the sheet's editor itself (/add_demo_client in
   * edit mode), not the export menu goExport opens.
   *
   * Two different asks wear the same name. From the drawer, 救命紙 is a
   * destination the adviser chose after reading the client: the menu, with the
   * sheet as its first card, is the right landing. From a row it is a shortcut,
   * and a shortcut that lands on a menu has spent the tap it saved — what the
   * adviser wants from a row is the sheet's own data, open and editable.
   *
   * `mode: 'edit'` is CreateDemoClient's own entry protocol, the same one
   * EmergencyExportView uses to get back to it; the page reads it off
   * location.state and then clears it. `selectClient` is what puts the client
   * in front of it — its edit branch fetches policies for `viewingClient`, so
   * without that it would open on whichever client was last selected.
   *
   * The page refuses 已中斷 clients (its own toast, since their policies are
   * not the adviser's to edit). No tap can reach that refusal from here: the
   * shortcut is disabled for exactly that status, on the `export` capability
   * this shares with the drawer's card.
   */
  const goSosEdit = (client: QueueClient) => {
    if (!deps.history) return;
    selectClient(client);
    deps.history.push({ pathname: '/add_demo_client', state: { mode: 'edit' } });
  };

  const goFactsheet = (client: QueueClient) => {
    const factsheetId = client.factsheetId?._id;
    if (!deps.history || !factsheetId) return;
    selectClient(client);
    deps.getClientDetails({ factsheetId, userId: client._id });
    deps.history.push({
      pathname: '/client_factsheet',
      clientId: client._id,
      factsheetId,
      state: { replaceRoute: '/client_factsheet' }
    });
  };

  const goReminder = (client: QueueClient) => {
    if (!deps.history) return;
    selectClient(client);
    const factsheet = client.factsheetId;
    deps.history.push({
      pathname: '/reminder',
      state: {
        clientFactsheetDetail: {
          clientIncData: {
            clientStatus: connectionStatusOf(client).status,
            name: factsheet?.name,
            phoneNumber: factsheet?.whatsapp,
            email: factsheet?.email,
            emailShow: !isSyntheticEmail(factsheet?.email, client._id)
          }
        }
      }
    });
  };

  return (action: ClientDrawerAction, sourceClient: QueueClient): void => {
    // ONE gate, in front of every action, keyed on the connection status
    // (clientEntitlements.ts). The UI already disables what a status withholds;
    // this is the backstop that makes the rule true even when a control is
    // reached another way — a stale drawer whose client was disconnected on the
    // server since it opened, a keyboard activation on a control the CSS greyed
    // out, or a future caller wiring this handler up somewhere new.
    if (!canClientAction(sourceClient, action)) return;

    // Delete opens the shared V1 dialog, which reads `inactiveUser`,
    // `trackingId` and `factsheetId._id` off the record to decide its warning
    // and what to send. It gets the raw client: those three are all
    // server-scoped to this adviser already, and the factsheet id must reach
    // the server or the adviser's factsheet is orphaned when the relationship
    // is unlinked.
    if (action === 'delete') {
      deps.toggleDialog('deleteClient', { client: sourceClient });
      return;
    }

    const client = actionableClientOf(sourceClient, deps.adviserEmail);
    const factsheet = client.factsheetId;

    switch (action) {
      case 'portfolio':
        goPortfolio(client);
        break;
      case 'add-policy':
        goAddPolicy(client);
        break;
      case 'export':
        goExport(client);
        break;
      case 'sos-edit':
        goSosEdit(client);
        break;
      case 'analysis':
        goAnalysis(client);
        break;
      case 'factsheet':
        goFactsheet(client);
        break;
      case 'smart-idea':
        deps.requirePro({
          customMessage: deps.intl.formatMessage({ id: 'upgrade-msg-smart-idea' }),
          cb: () => deps.mergeDialog('smartIdea', { closed: true })
        });
        deps.toggleDialog('smartIdea', { client, communicationTab: true });
        break;
      // The drawer's DISC readout. Same dialog and the SAME PRO gate — this is
      // 智能 IDEA either way, and a second door into it cannot be a cheaper one —
      // opened marked on the card that carries what the readout reports. The
      // small DISC picker stays reachable from the ⓘ beside that readout, so an
      // adviser without PRO can still set a personality.
      case 'smart-idea-disc':
        deps.requirePro({
          customMessage: deps.intl.formatMessage({ id: 'upgrade-msg-smart-idea' }),
          cb: () => deps.mergeDialog('smartIdea', { closed: true })
        });
        deps.toggleDialog('smartIdea', { client, focusSection: 'personality', communicationTab: true });
        break;
      case 'meeting-note':
        deps.toggleDialog('meeting', meetingDialogContent(client, 'note'));
        break;
      // The row's shortcut: same dialog, opened with its recorder already up.
      // `triggerRecordDialog` is MeetingDialog's own flag (it opens the
      // recorder once the dialog has settled, then clears the flag itself) —
      // the same one ChatGPTOptions' 語音筆記 uses, so both doors into voice
      // notes go through one mechanism rather than two.
      case 'meeting-record':
        deps.toggleDialog('meeting', { ...meetingDialogContent(client, 'note'), triggerRecordDialog: true });
        break;
      // The 上次會面 readout's own tap: same dialog, opened onto the row that
      // readout reports, because the number being wrong or missing is the only
      // reason to open it from there.
      case 'meeting-date':
        deps.toggleDialog('meeting', meetingDialogContent(client, 'note', 'latestMeetingDate'));
        break;
      // The 提醒 readout's own tap, on the same terms: the dialog opens marked
      // on the 提醒我 row it reports, so the adviser lands looking at the field
      // they tapped instead of hunting for it in a form of eight.
      case 'meeting-reminder':
        deps.toggleDialog('meeting', meetingDialogContent(client, 'note', 'fsReminderDate'));
        break;
      case 'tracking':
        if (!client.trackingId?.isActive) {
          deps.toggleDialog('trackOn', {
            client,
            startDate: moment(),
            expectedBusinessAmount: ''
          });
        } else {
          deps.toggleDialog('trackOff', {
            client,
            businessVol: client.trackingId.businessVol ?? null,
            met: client.trackingId.met ?? 0
          });
        }
        break;
      case 'tracking-summary':
        deps.toggleDialog('meeting', meetingDialogContent(client, 'summary'));
        break;
      case 'contact':
        deps.toggleDialog('contact', { client });
        break;
      case 'reminder':
        goReminder(client);
        break;
      case 'referral':
        if (!deps.requirePro()) return;
        deps.history?.replace({
          ...(deps.location || {}),
          state: {
            ...((deps.location?.state as Record<string, unknown> | undefined) || {}),
            client
          }
        });
        deps.toggleDialog('referralQRCode', {
          referLinkSearch: {
            defaultMessage: deps.intl.formatMessage(
              { id: 'client-referral-qr-default-msg' },
              { adviserName: deps.userDetails?.name, clientName: factsheet?.name }
            )
          },
          customCTAMessage: deps.intl.formatMessage({ id: 'referral-card-message-2' }),
          defaultMessageIntlId: 'referral-link-message-2',
          defaultMessageExtras: { name: deps.userDetails?.name }
        });
        break;
      case 'product-recommendation':
        if (deps.requirePro()) deps.onProductRecommendation(client);
        break;
      // Both doors onto /gpt-options. They differ only in what the page is told
      // it was opened for: 招募 view sends `mode: 'recruit'` and gets the
      // recruitment tools, 業務 sends nothing and gets the page as it has
      // always been. A separate action rather than a third argument to
      // `onAction`, whose two-argument shape is relied on in fifteen places.
      case 'ai':
      case 'ai-recruit': {
        const recruitMode = action === 'ai-recruit';
        // Without FEATURE_GPT or Ultra, /gpt-options sends the adviser to /home
        // (ChatGPTOptions) and the client is lost. The greyed card shows the same
        // upgrade dialog the other paid tools use, but its button opens a WhatsApp
        // to us with the request already written — A.I. is switched on by us, not
        // bought on /subscription (and iOS cannot buy there at all).
        if (deps.aiAvailable === false) {
          const mode = recruitMode ? 'recruit' : 'business';
          deps.track?.('Click on Locked ChatGPT - Client Page', { mode });
          deps.requireUltra?.({
            customMessage: deps.intl.formatMessage({ id: 'upgrade-msg-ai' }),
            planMessageId: 'pro-dialog-msg-ultra',
            upgradeLabelId: 'pro-dialog-contact-us',
            onUpgrade: () => {
              deps.track?.('Click on A.I. Access WhatsApp - Client Page', { mode });
              deps.trackSupport?.({
                msg_channel: 'whatsapp',
                channel_targert: SUPPORT_WHATSAPP,
                support_type: 'ai_access'
              });
              window.open(aiAccessWhatsAppUrl(deps.intl), '_blank');
            }
          });
          break;
        }
        // One event name with the conversation as a property, rather than a
        // second name. `Click on ChatGPT - Client Page` is a live series that
        // V1's client page still feeds (ClientEntry.js), so splitting the V2
        // half off under a new name would have shown every dashboard counting
        // it losing traffic — which reads as advisers using the feature less,
        // not as a rename, and there is nothing in the data to say otherwise.
        deps.track?.('Click on ChatGPT - Client Page', { mode: recruitMode ? 'recruit' : 'business' });
        // Selected BEFORE the branch, not inside it. The disclaimer dialog is
        // shown once per device and navigates to /gpt-options by itself
        // (ChatGPTDisclaimerDialog) — without the client. That page reads the
        // client from redux, so the first adviser ever to tap this arrived on
        // whoever they had open last. Selecting up front costs nothing if they
        // cancel: they are looking at this client either way.
        selectClient(client);
        if (!localStorage.getItem('showedGPTDisclaimer')) {
          // The mode has to survive this detour too. The dialog does the
          // navigating on the adviser's first ever tap, so leaving it out here
          // would mean recruitment mode silently did nothing exactly once —
          // for every adviser, on the one visit where they are least able to
          // tell a missing feature from a feature they have not found yet.
          deps.toggleDialog('chatGPTDisclaimer', recruitMode ? { mode: 'recruit' } : {});
        } else if (deps.history) {
          deps.history.push(recruitMode ? { pathname: '/gpt-options', state: { mode: 'recruit' } } : '/gpt-options');
        }
        break;
      }
      // 行業材料 — the same 行業前景 library the 招募 page opens
      // (Recruitment.js:78-88), reached from the candidate instead of from the
      // team menu. No client is passed because the dialog has none to take:
      // it is a chart library, and what makes it the right one for THIS person
      // is the adviser holding the drawer open beside it.
      case 'recruit-materials':
        deps.track?.('Click on Recruitment Materials - Client Drawer');
        deps.toggleDialog('recruitment', {});
        break;
      // The same dialog, opened on its calculator instead of its front page.
      // `focusItem` is RecruitmentContentsDialog's own entry protocol.
      case 'mdrt-calculator':
        deps.track?.('Click on MDRT Calculator - Client Drawer');
        deps.toggleDialog('recruitment', { focusItem: RECRUITMENT_MDRT_ITEM_ID });
        break;
      // 建團隊收入模擬器 — the same dialog, on the simulator beside the calculator.
      case 'income-simulator':
        deps.track?.('Click on Income Simulator - Client Drawer');
        deps.toggleDialog('recruitment', { focusItem: RECRUITMENT_INCOME_SIM_ITEM_ID });
        break;
      // 招聘懶人包 — the 懶人包 page, scrolled to its 招聘 & 成長 row. That row
      // is last of six, so a plain push lands the adviser at the top of a page
      // whose recruitment half is a screen and a half below the fold.
      case 'recruit-hot-topic':
        deps.track?.('Click on Recruitment Hot Topic - Client Drawer');
        deps.history?.push({
          pathname: '/hot_topic',
          state: { scrollToCategory: RECRUITMENT_HOT_TOPIC_CATEGORY_ID }
        });
        break;
      // TeamHub — the existing team library. The drawer is only a new door;
      // browsing, uploading and permissions stay owned by that mature page.
      case 'team-hub':
        deps.track?.('Click on TeamHub - Client Drawer');
        deps.history?.push('/team_library');
        break;
      // The existing team-scoped assistant used by the 招募 landing page.
      case 'team-bot':
        deps.track?.('Click on Team Bot - Client Drawer');
        deps.toggleDialog('chatBot', {});
        break;
      // AI Coach is the existing AI Drill surface, renamed only at this door.
      case 'ai-coach':
        deps.track?.('Click on AI Coach - Client Drawer');
        deps.history?.push('/new/media-analysis');
        break;
      // IIQE 備試 — pfpdata's exam study tool. Opens in its own window rather
      // than the embedded-iframe door the other actions use: the study tool
      // is a full app in its own right, not a panel meant to sit inside ours.
      // `window.open` alone would just navigate the SAME webview on the
      // Capacitor app (there is no second tab to open there), so the native
      // build gets the in-app Browser overlay instead — the same split every
      // other external-link opener in this codebase uses (see
      // utils/native/utils.js, VideoPlayer.js).
      case 'iiqe-study': {
        deps.track?.('Click on IIQE Study - Client Drawer');
        const iiqeUrl = 'https://beta-data.portfoplus.com/iiqe-study';
        if (NativeOrWeb.isNativePlatform()) {
          Browser.open({ url: iiqeUrl });
        } else {
          window.open(iiqeUrl, '_blank');
        }
        break;
      }
      default:
        break;
    }
  };
};

export const openConnectionDialog = (
  toggleDialog: ClientActionDependencies['toggleDialog'],
  client: QueueClient,
  adviserEmail?: string
): void => {
  const actionableClient = actionableClientOf(client, adviserEmail);
  toggleDialog(actionableClient.inactiveUser && !actionableClient.language ? 'chooseLanguage' : 'connection', {
    client: actionableClient
  });
};

export const openBirthdayDialog = (
  toggleDialog: ClientActionDependencies['toggleDialog'],
  intl: IntlLike,
  client: QueueClient,
  adviserEmail?: string
): void => {
  if (!canClientAction(client, 'edit-profile')) return;
  const actionableClient = actionableClientOf(client, adviserEmail);
  const factsheet = actionableClient.factsheetId;
  const dob = factsheet?.dob || (connectionStatusOf(actionableClient).status !== 'Disconnected' ? client.dob : null);
  if (!dob) return;
  // `parseZone`, for the same reason the drawer's own 生日 readout uses it
  // (`describeBirthday`): the server stores a dob as a Date, so it arrives as
  // UTC midnight, and a plain `moment` re-reads that in the browser's zone and
  // lands a day early for every adviser behind UTC. This dialog opens FROM the
  // cell that readout sits in, and since 2026-09-02 the two print the same ISO
  // shape — so a one-day disagreement between them is now plainly visible
  // rather than hidden behind two different formats.
  const dobMoment = moment.parseZone(dob);
  // The age comes from `getAgeFromDob` and nothing else, for the reason
  // ClientV2Bridge gives where it WRITES this same pair: that function is
  // mirrored on the server (server/utils/date.js), so it is the age the record
  // carries, the age `incomeDataOf` sends with a product recommendation two
  // functions up, and the age the C.P.I. scores. A locally-derived number here
  // would be a second age for one dob, differing from the stored one on the
  // birthday itself for any adviser behind UTC — and the dialog would be the
  // only surface in the app disagreeing with the record it opened from.
  //
  // It normalises to LOCAL midnight before flooring, so it does not carry the
  // birthday-morning error `moment().diff(parseZone(dob), 'years')` has; the
  // day it resolves that midnight from is the one open question, and that is a
  // change to make on both sides of the wire at once, not here.
  const age = getAgeFromDob(dob, undefined);
  toggleDialog('greeting', {
    title: intl.formatMessage({ id: 'Greeting' }),
    content: (
      <div style={{ textAlign: 'center' }}>
        <img alt="" className="imageClientState" src="img/birthdayClient.svg" />
        <span>
          {/* ISO, the shape the 生日 cell this opens from prints and the shape
              the record stores. */}
          {dobMoment.format('YYYY-MM-DD')} ({age})
        </span>
      </div>
    ),
    phone: factsheet?.phoneNumber,
    whatsapp: factsheet?.whatsapp,
    email: isSyntheticEmail(factsheet?.email, actionableClient._id) ? '' : factsheet?.email,
    getMessage: (language: string) => greetingMessage(actionableClient, language)
  });
};

export const openPersonalityDialog = (
  toggleDialog: ClientActionDependencies['toggleDialog'],
  updateFactSheet: ClientActionDependencies['updateFactSheet'],
  client: QueueClient,
  adviserEmail?: string
): void => {
  if (!canClientAction(client, 'edit-profile')) return;
  const actionableClient = actionableClientOf(client, adviserEmail);
  const factsheet = actionableClient.factsheetId;
  toggleDialog('DISC', {
    personality: factsheet?.personality,
    onSubmit: (personality: string) =>
      updateFactSheet({
        id: factsheet?._id,
        key: 'personality',
        value: personality,
        clientId: actionableClient._id
      })
  });
};
